<!-- src/apps/session/components/AuthView.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import CornerBrackets from '@/shared/components/ui/CornerBrackets.vue';
  import {
    useJurisdictionStore,
    useJurisdictionDisplayNames,
  } from '@/shared/stores/jurisdictionStore';
  import { computed } from 'vue';

  interface Props {
    heading: string;
    headingId: string;
    title?: string | null;
    titleLogo?: string | null;
    withHeading?: boolean;
    withSubheading?: boolean;
    hideIcon?: boolean;
    /**
     * Omit the title icon/logo block entirely (v-if), reclaiming its space.
     * Differs from `hideIcon`, which only makes the icon `invisible` to
     * preserve vertical alignment. Used by focused status pages that want the
     * heading at the top with no brand mark competing for attention.
     */
    omitIcon?: boolean;
    hideBackgroundIcon?: boolean;
    showReturnHome?: boolean;
    /** Mono uppercase label in the terminal panel's title bar */
    panelLabel?: string | null;
  }

  // Define props with defaults
  const props = withDefaults(defineProps<Props>(), {
    title: null,
    titleLogo: null,
    withHeading: true,
    withSubheading: false,
    hideIcon: false,
    omitIcon: false,
    hideBackgroundIcon: false,
    showReturnHome: true,
    panelLabel: null,
  });

  const { t } = useI18n();

  // Initialize jurisdiction store
  const jurisdictionStore = useJurisdictionStore();
  const { currentJurisdictionWithDisplayName } = useJurisdictionDisplayNames();

  // Compute the current jurisdiction or default to unknown
  // Uses resolved display_name from i18n
  const currentJurisdiction = computed(() =>
    currentJurisdictionWithDisplayName.value || {
      identifier: t('web.regions.unknown_jurisdiction'),
      display_name_i18n_key: 'web.regions.unknown_jurisdiction',
      display_name: t('web.regions.unknown_jurisdiction'),
      domain: '',
      icon: {
        collection: 'mdi',
        name: 'help-circle',
      },
      enabled: false,
    }
  );

  const effectivePanelLabel = computed(() => props.panelLabel || t('auth.terminal_label'));
</script>

<template>
  <!--
    seuid.com.br auth scaffold (Figma Make): centered column over the faded
    48px grid + brand glow, brand tile above a mono heading, and the form
    inside a terminal panel (title bar + corner brackets).
  -->
  <div
    class="relative flex min-h-screen items-start justify-center overflow-hidden bg-day px-4 pt-12 dark:bg-night sm:px-6 sm:pt-16 lg:px-8">
    <!-- Grid + glow backdrop (suppressed on branded/custom-domain pages) -->
    <template v-if="!hideBackgroundIcon">
      <div
        class="pointer-events-none absolute inset-0
          bg-[linear-gradient(rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.05)_1px,transparent_1px)]
          bg-[size:48px_48px]
          [mask-image:radial-gradient(ellipse_at_50%_50%,black_30%,transparent_80%)]
          dark:bg-[linear-gradient(rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.07)_1px,transparent_1px)]"
        aria-hidden="true"></div>
      <!--
        Decorative brand glow. Uses the live palette token instead of a frozen
        rgba so it follows per-domain branding (and it no longer drifts, as it
        did when the brand blue moved from #4D8EFF to #2563EB).
      -->
      <div
        class="pointer-events-none absolute left-1/2 top-[30%] h-[400px] w-[600px] -translate-x-1/2 -translate-y-1/2 opacity-[0.06] bg-[radial-gradient(ellipse,var(--color-brand-500)_0%,transparent_70%)]"
        aria-hidden="true"></div>
    </template>

    <!-- Page Title -->
    <div class="relative z-10 w-full min-w-[320px] max-w-md space-y-10">
      <!-- Title Icon / Logo -->
      <div
        v-if="!omitIcon"
        class="flex flex-col items-center"
        :class="{ invisible: hideIcon }">
        <RouterLink
          to="/"
          class="group"
          :aria-label="t('web.layout.return_to_home_page')">
          <!-- Custom logo (for branded/custom domain pages) -->
          <template v-if="titleLogo">
            <img
              :src="titleLogo"
              :alt="title ?? ''"
              class="h-16 max-w-[200px] object-contain transition-transform duration-300 group-hover:scale-105" />
          </template>
          <!-- Brand tile (Figma Make auth mark) -->
          <template v-else>
            <div
              class="flex size-12 items-center justify-center border border-day-line-2 bg-day-100 transition-transform duration-300 group-hover:scale-105 dark:border-white/[0.13] dark:bg-night-100">
              <!-- Runtime-bound (not static src): /favicon.svg is served by the
                   backend's brand-pack overlay, not a bundled asset. -->
              <img
                :src="'/favicon.svg'"
                alt=""
                class="size-7"
                aria-hidden="true" />
            </div>
          </template>
        </RouterLink>
      </div>

      <!-- Title Text -->
      <div class="space-y-2 text-center">
        <h1
          :id="headingId"
          v-if="withHeading"
          class="font-mono text-xl font-bold tracking-[-0.02em] text-day-ink dark:text-night-ink">
          {{ heading }}
        </h1>
        <p
          v-if="withSubheading"
          class="flex items-center justify-center gap-1 font-mono text-[10px] uppercase tracking-[0.08em] text-day-ink-faint dark:text-night-ink-faint">
          <template v-if="!titleLogo">
            <span class="font-bold normal-case text-day-ink dark:text-night-ink"
              >seu<span class="text-brand-500">id</span></span
            >
            <span aria-hidden="true">·</span>
          </template>
          <span v-if="jurisdictionStore.enabled">
            {{ currentJurisdiction.display_name }}
          </span>
          <span v-else>{{ t('landing.terminal.secure_channel') }}</span>
        </p>
      </div>

      <!-- Form Panel (terminal chrome) -->
      <div
        role="region"
        :aria-labelledby="headingId"
        class="auth-panel relative border border-day-line-2 bg-day
          shadow-[0_0_0_1px_rgba(0,0,0,0.02),0_32px_80px_rgba(0,0,0,0.12)]
          [--field-bg:#ffffff] [--field-border:rgba(17,24,39,0.2)]
          [--field-ink:#111827] [--label-ink:#6b7280]
          dark:border-white/[0.13] dark:bg-night-100
          dark:shadow-[0_32px_80px_rgba(0,0,0,0.7)]
          dark:[--field-bg:#0a0a10] dark:[--field-border:rgba(255,255,255,0.13)]
          dark:[--field-ink:#f4f4f8] dark:[--label-ink:#8a8aa8]">
        <!-- title bar -->
        <div
          class="flex h-9 items-center gap-2.5 border-b border-day-line bg-day-200 px-3.5
            dark:border-white/[0.13] dark:bg-night-200">
          <span
            class="inline-block size-[5px] shrink-0 rounded-full bg-brand-500 shadow-[0_0_6px_var(--color-brand-500)]"
            aria-hidden="true"></span>
          <span
            class="font-mono text-[9px] uppercase tracking-[0.1em] text-day-ink-faint dark:text-night-ink-faint">
            {{ effectivePanelLabel }}
          </span>
        </div>

        <div class="p-5 sm:p-6">
          <slot name="form"></slot>
        </div>

        <CornerBrackets />
      </div>

      <!-- Footer -->
      <div class="space-y-5 pb-10 text-center">
        <!--
          Mono support text (Figma Make): the slot content keeps its own
          markup; typography/link color come from the scoped rules below.
          brand-600 in light mode keeps the 10px links at 4.5:1.
        -->
        <div
          class="auth-footer font-mono text-[10px] tracking-[0.04em] text-day-ink-faint
            [--auth-link:var(--color-brand-600)]
            dark:text-night-ink-faint dark:[--auth-link:var(--color-brand-500)]">
          <slot name="footer"></slot>
        </div>

        <!-- Subtle home link for escape route -->
        <div v-if="showReturnHome">
          <RouterLink
            to="/"
            class="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.06em] text-day-ink-faint transition-colors duration-200 hover:text-gray-700 dark:text-white/25 dark:hover:text-night-ink-faint"
            :aria-label="t('web.layout.return_to_home_page')">
            <span aria-hidden="true">←</span>
            <span>{{ t('web.layout.return_home') }}</span>
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  /*
   * Terminal identity for the slotted auth forms (SignInForm, SignUpForm,
   * password reset, MFA…): mono micro-labels, flat near-black fields and a
   * mono uppercase primary button — applied here once instead of forking
   * every form component. The --field-* / --label-ink variables are set on
   * .auth-panel via Tailwind arbitrary properties and flip with dark mode.
   */
  .auth-panel :deep(label) {
    font-family: var(--font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--label-ink);
  }

  .auth-panel :deep(input:not([type='checkbox']):not([type='radio'])),
  .auth-panel :deep(select),
  .auth-panel :deep(textarea) {
    background-color: var(--field-bg);
    border-radius: 0;
    border-color: var(--field-border);
    font-family: var(--font-mono);
    font-size: 13px;
    color: var(--field-ink);
  }

  .auth-panel :deep(button[type='submit']) {
    border-radius: 0;
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  /* In-form support links ("Esqueceu sua senha?", alternate methods) */
  .auth-panel :deep(a) {
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.04em;
  }

  /* Footer support text: mono 10px with quiet links, as in the mock */
  .auth-footer :deep(a),
  .auth-footer :deep(span) {
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.04em;
  }

  .auth-footer :deep(a) {
    color: var(--auth-link);
    text-decoration: none;
  }

  .auth-footer :deep(a:hover) {
    text-decoration: underline;
  }
</style>
